import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { SMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

const ReturnRequestUrlModal = NiceModal.create(({ order }) => {
	const modal = useModal();
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [returnRequestUrl, setReturnRequestUrl] = useState(null);
	const [error, setError] = useState(null);

	const axiosPrivate = useAxiosPrivate();
	const { showError } = useAlert();

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					returnLink: yup.string().nullable(),
					email: yup.string().email().nullable(),
					phone: yup.string().nullable()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			email: null,
			phone: null
		}
	});

	const { watch, setValue } = formMethods;

	const getRequestLink = async (id) => {
		setIsLoading(true);
		await axiosPrivate
			.post(SMS.GET_RETURN_REQUEST_LINK, { orderId: id })
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					setReturnRequestUrl(data.returnLink ?? null);
				} else {
					showError({
						message: 'errorMsg.returnRequestUrl'
					});
					AppLogger.error(`ReturnRequestUrlModal: ${response}`);
				}
			})
			.catch((error) => {
				setError(error);
				if (error?.response?.data?.code === 'error.C021') {
					showError({
						header: 'errorMsg.returnRequestUrl',
						message: 'error.C021',
						link: 'addSMSSettings',
						linkAction: () => navigate(PATHS.SMS_SETTINGS)
					});
				} else if (error?.response?.data?.code === 'error.C026') {
					showError({
						header: 'errorMsg.returnRequestUrl',
						message: 'error.C026',
						link: 'addSettings',
						linkAction: () => navigate(PATHS.CUSTOMIZABLE_PAGES)
					});
				} else {
					AppLogger.error(`ReturnRequestUrlModal: ${error}`);
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		setValue('phone', order.phone || null);
		setValue('email', order.email || null);

		getRequestLink(order.ID);

		return () => {
			setReturnRequestUrl(null);
		};
	}, []);

	useEffect(() => {
		if (error) {
			modal.remove();
		}
	}, [error]);

	const defaultColSpan = {
		md: 12
	};

	const phone = watch('phone');
	const email = watch('email');

	return (
		<>
			{returnRequestUrl ? (
				<Modal fullwidth maxWidth="small" className="modal-getReturnRequestUrl">
					<ModalTitle>
						<Typography variant="p1">
							{t('getReturnRequestUrl')} (<strong>{order?.orderID}</strong>)
						</Typography>
					</ModalTitle>
					<ModalContent>
						<Form formMethods={formMethods}>
							{phone && (
								<FormItem colSpan={defaultColSpan} label="customerPhoneNumber" isLoading={isLoading}>
									<CopyComponent value={phone} />
								</FormItem>
							)}
							{email && (
								<FormItem colSpan={defaultColSpan} label="customerEmail" isLoading={isLoading}>
									<CopyComponent value={email} />
								</FormItem>
							)}
							<FormItem
								label="returnRequestUrl"
								colSpan={defaultColSpan}
								toolTip="tooltip.returnRequestUrl"
								isLoading={isLoading}>
								<CopyComponent value={returnRequestUrl} share toEmail={email || ''} />
							</FormItem>
						</Form>
					</ModalContent>

					<ModalActions>
						<Button label="close" variant="outline" onClick={handleCloseModal} />
					</ModalActions>
				</Modal>
			) : (
				isLoading && <Loading />
			)}
		</>
	);
});

export default ReturnRequestUrlModal;
